import { Button, Cascader, Form, Input, Select, Space, type FormInstance } from "antd";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    CheckboxGroup as ZhCheckboxGroup,
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber,
    Select as ZhSelect
} from "zhihao-ui-react";

import request from "@/api";
import { areaLib } from "@/utils/areaLib";
import { ADVERTISING_WATCH_TIME, IS_INCLUDE_TAX, TAX_RATE, WAITING_PAY_TIME } from "@zhihaoscm/meta";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import {
    AddressAutocomplete,
    getSuggestionAreaCodes,
    getSuggestionGeo,
    mapAreaCascaderOptions,
    type AreaCascaderOption
} from "@/views/components/AddressAutocomplete";
import WangEditor, { type RichEditorRef } from "@/views/components/WangEditor";
import {
    DEFAULT_PRODUCT_PARAMETERS,
    DEFAULT_SERVICE_GUARANTEE,
    DEFAULT_SETTLEMENT_DESCN,
    FormSection,
    MediaUploadCard,
    OfflineAccountEditor,
    OnlineAccountTip,
    ParameterEditor,
    ProductLabelEditor
} from "./ConsignmentFormPage";
import type { BargainFormValues, BargainRow } from "./types";
import "./ConsignmentFormPage.scss";

type BargainMediaFormValues = BargainFormValues;

interface BargainUploadedFile {
    id: number;
    path?: string;
}

export interface BargainFormPageProps {
    loadBargain?: (id: number | string) => Promise<BargainRow>;
    saveBargain?: (id: number | string | undefined, values: BargainMediaFormValues) => Promise<void>;
}

/** 复用 Vue 捡漏商品详情接口，为编辑表单回填数据。 */
export async function loadBargain(id: number | string): Promise<BargainRow> {
    const response = await request.service.get<BargainRow>(`/admin/product-bargain/vo/${id}`);
    return response.data;
}

/** 复用 Vue 捡漏商品新增草稿和更新草稿接口。 */
export async function saveBargain(id: number | string | undefined, values: BargainMediaFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-bargain/create-save", values);
    } else {
        await request.service.post(`/admin/product-bargain/update-save/${id}`, values);
    }
}

/** 复用 Vue upload-list 的文件接口，返回后端文件 ID 和访问路径。 */
export async function uploadBargainMedia(file: Blob): Promise<BargainUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

const IMAGE_MAX_SIZE = 2 * 1024 * 1024;
const VIDEO_MAX_SIZE = 200 * 1024 * 1024;

/** 按 Vue 上传组件的扩展名和 MIME 类型校验图片或视频。 */
function validateBargainMedia(file: File, mediaType: "image" | "video"): string | null {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (mediaType === "image") {
        const validType = file.type === "image/jpeg" || file.type === "image/png";
        const validExtension = extension === "jpg" || extension === "jpeg" || extension === "png";
        if (!validType || !validExtension) return "主图仅支持 JPEG、PNG 格式";
        if (file.size > IMAGE_MAX_SIZE) return "主图大小不能超过 2MB";
    } else {
        const validType = file.type === "video/mp4" || file.type === "video/quicktime";
        const validExtension = extension === "mp4" || extension === "mov";
        if (!validType || !validExtension) return "视频仅支持 MOV、MP4 格式";
        if (file.size > VIDEO_MAX_SIZE) return "视频大小不能超过 200MB";
    }
    return null;
}

/** Vue 表单中的 wangEditor 迁移为受控组件，隐藏字段只保留 Ant Form 的数据接入。 */
function BargainRichTextItem({
    form,
    name,
    label,
    required = false,
    editorRef
}: {
    form: FormInstance<BargainMediaFormValues>;
    name: keyof BargainMediaFormValues;
    label: string;
    required?: boolean;
    editorRef?: RefObject<RichEditorRef | null>;
}) {
    const value = Form.useWatch(name, form);
    return (
        <div className="consignment-form__rich-row">
            <label className={`consignment-form__label${required ? " consignment-form__label--required" : ""}`}>
                {label}
            </label>
            <div className="consignment-form__rich-content">
                <WangEditor
                    ref={editorRef}
                    value={typeof value === "string" ? value : ""}
                    height="300px"
                    onUpdateValue={next => form.setFieldValue(name, next)}
                />
                <Form.Item name={name} noStyle>
                    <Input.TextArea aria-label={label} className="consignment-form__legacy-control" />
                </Form.Item>
            </div>
        </div>
    );
}

/** 捡漏商品新增/编辑页，覆盖 Vue 表单的核心价格、数量、时间和介绍字段。 */
export function BargainFormPage({
    loadBargain: load = loadBargain,
    saveBargain: save = saveBargain
}: BargainFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || undefined;
    const [form] = Form.useForm<BargainMediaFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mediaIds, setMediaIds] = useState<{ imgMainId?: number; videoId?: number }>({});
    const [mediaPaths, setMediaPaths] = useState<{ imgMainPath?: string; videoPath?: string }>({});
    const bargainUnitPrice = Form.useWatch("bargainUnitPrice", form);
    const bargainTon = Form.useWatch("bargainTon", form);
    const bargainStartTime = Form.useWatch("bargainStartTime", form);
    const bargainCycle = Form.useWatch("bargainCycle", form);
    const isIncludeTax = Form.useWatch("isIncludeTax", form);
    const payTypes = Form.useWatch("payTypes", form);
    const regionCode = Form.useWatch("regionCode", form);
    const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
    // 线下收款账号独立于 Ant Form 字段维护，与旧版的附加账号编辑器一致。
    const [offlineAccounts, setOfflineAccounts] = useState<NonNullable<BargainRow["productBargain"]["accountInfo"]>>([
        {}
    ]);
    const [supplierOptions, setSupplierOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<
        Array<{ label: string; value: string; tags?: string[] }>
    >([]);
    const [productTypeTags, setProductTypeTags] = useState<string[]>([]);
    const introduceEditorRef = useRef<RichEditorRef>(null);
    const deliveryEditorRef = useRef<RichEditorRef>(null);
    const settlementEditorRef = useRef<RichEditorRef>(null);
    const serviceEditorRef = useRef<RichEditorRef>(null);
    const regulationEditorRef = useRef<RichEditorRef>(null);

    useEffect(() => {
        // 复用全局地区库生成交付地区三级级联选项。
        void areaLib.ready.then(() => setAreaOptions(mapAreaCascaderOptions(areaLib.getAreaList())));
    }, []);
    // 沿用 Vue 表单的实时计算口径：捡漏单价乘以捡漏吨数，未形成有效金额时显示占位符。
    const totalCost = useMemo(() => {
        const unitPrice = Number(bargainUnitPrice);
        const ton = Number(bargainTon);
        if (!Number.isFinite(unitPrice) || !Number.isFinite(ton) || !unitPrice || !ton) return null;
        return unitPrice * ton;
    }, [bargainTon, bargainUnitPrice]);
    // 旧版截止时间由开始时间和活动天数派生，不能让用户单独编辑造成数据不一致。
    const bargainEndTime = useMemo(() => {
        if (!bargainStartTime) return "--";
        const end = new Date(bargainStartTime.replace(/-/g, "/"));
        if (Number.isNaN(end.getTime())) return "--";
        end.setDate(end.getDate() + Number(bargainCycle || 0));
        const pad = (value: number) => String(value).padStart(2, "0");
        return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())} ${pad(end.getHours())}:${pad(end.getMinutes())}:${pad(end.getSeconds())}`;
    }, [bargainCycle, bargainStartTime]);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({
                title: "",
                weight: 0,
                parameterInfo: DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item })),
                productLabel: [""],
                payTypes: [1],
                settlementDescn: DEFAULT_SETTLEMENT_DESCN,
                serviceGuarantee: DEFAULT_SERVICE_GUARANTEE,
                regulation:
                    "1、捡漏专区选带中“正在捡漏”商品，点“立即抢漏”报名。\n2、生成专属链接邀好友助力。\n3、活动期间内助力人数排名第一的用户获得低价购买资格。"
            });
            setMediaIds({});
            setMediaPaths({});
            setOfflineAccounts([{}]);
            return;
        }

        void load(id)
            .then(result => {
                setErrorMessage(null);
                const product = result.productBargain as BargainMediaFormValues;
                // 编辑回显优先使用表单字段，兼容 Vue 详情接口将文件 ID 放在文件对象中的返回结构。
                const imgMainId = product.imgMainId ?? result.mainFile?.id;
                const videoId = product.videoId ?? result.videoFile?.id;
                setMediaIds({ imgMainId, videoId });
                setMediaPaths({ imgMainPath: result.mainFile?.path, videoPath: result.videoFile?.path });
                if (product.supplierId != null) {
                    const supplierName =
                        result.productBargain.supplierEnterprise?.supplierName ||
                        result.productBargain.supplierEnterprise?.enterpriseName ||
                        result.productBargain.supplierEnterprise?.name;
                    setSupplierOptions([
                        { value: product.supplierId, label: supplierName || String(product.supplierId) }
                    ]);
                }
                if (product.productTypeId && result.productType?.name) {
                    const typeLabel = result.productType.areaAbbreviation
                        ? `${result.productType.name} | ${result.productType.areaAbbreviation}`
                        : result.productType.name;
                    const tags = result.productTypeVo?.tags?.map(tag => tag.name || "").filter(Boolean) || [];
                    setProductTypeOptions([{ value: product.productTypeId, label: typeLabel, tags }]);
                    setProductTypeTags(tags);
                }
                product.parameterInfo ||= DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item }));
                product.productLabel ||= [""];
                product.settlementDescn ||= DEFAULT_SETTLEMENT_DESCN;
                product.serviceGuarantee ||= DEFAULT_SERVICE_GUARANTEE;
                // 详情接口以 payType=3 表示两种方式，编辑控件需要还原为独立选项。
                const storedPayType = Number(product.payType);
                if (!product.payTypes?.length && [1, 2, 3].includes(storedPayType)) {
                    product.payTypes = storedPayType === 3 ? [1, 2] : [storedPayType];
                }
                setOfflineAccounts(
                    result.productBargain.accountInfo?.length ? result.productBargain.accountInfo : [{}]
                );
                form.setFieldsValue({
                    ...product,
                    areaCode: [product.provinceCode, product.cityCode, product.regionCode].filter(Boolean) as string[]
                });
            })
            .catch(() => setErrorMessage("捡漏商品详情加载失败"));
    }, [form, id, load]);

    /** 上传单个媒体文件，并将 Vue 契约中的文件 ID回填到待保存状态。 */
    const handleMediaChange = async (file: File | undefined, mediaType: "image" | "video") => {
        if (!file) return;
        const validationError = validateBargainMedia(file, mediaType);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        setErrorMessage(null);
        try {
            const uploadedFile = await uploadBargainMedia(file);
            if (!uploadedFile.id) throw new Error("missing file id");
            if (mediaType === "image") {
                setMediaIds(current => ({ ...current, imgMainId: uploadedFile.id }));
                setMediaPaths(current => ({ ...current, imgMainPath: uploadedFile.path }));
            } else {
                setMediaIds(current => ({ ...current, videoId: uploadedFile.id }));
                setMediaPaths(current => ({ ...current, videoPath: uploadedFile.path }));
            }
        } catch {
            setErrorMessage(`捡漏商品${mediaType === "image" ? "主图" : "视频"}上传失败`);
        }
    };

    const submit = async () => {
        // 每次提交先清理上一次错误，避免校验失败后重复展示过期的保存失败提示。
        setErrorMessage(null);
        try {
            const values = await form.validateFields();
            // 仅在用户已有文件或完成上传时附加 ID，确保未涉及媒体时保存 payload 保持原字段集合。
            const payload: BargainMediaFormValues = Object.fromEntries(
                Object.entries(values).filter(([key, value]) => {
                    if (value === undefined) return false;
                    if (key === "areaCode") return false;
                    if (key === "productLabel" && Array.isArray(value) && value.every(item => !item)) return false;
                    if (
                        key === "parameterInfo" &&
                        Array.isArray(value) &&
                        value.every(item => item && typeof item === "object" && !item.parameterValue)
                    )
                        return false;
                    if (key === "settlementDescn" && value === DEFAULT_SETTLEMENT_DESCN) return false;
                    if (key === "serviceGuarantee" && value === DEFAULT_SERVICE_GUARANTEE) return false;
                    return true;
                })
            ) as BargainMediaFormValues;
            if (mediaIds.imgMainId != null) payload.imgMainId = mediaIds.imgMainId;
            if (mediaIds.videoId != null) payload.videoId = mediaIds.videoId;
            // 后端沿用旧版单值 payType 契约；仅线下支付需要提交关联账号。
            if (payload.payTypes?.length) {
                payload.payType =
                    payload.payTypes.includes(1) && payload.payTypes.includes(2) ? 3 : payload.payTypes[0];
                payload.platformBankAccountForm = payload.payTypes.includes(2) ? offlineAccounts : undefined;
            }
            await save(id, payload);
            void navigate("/productManagement/bargain");
        } catch (error) {
            // Ant Form 校验失败已在字段旁展示具体原因，不再追加泛化的保存失败提示。
            if (!(error as { errorFields?: unknown[] }).errorFields) setErrorMessage("捡漏商品保存失败");
        }
    };

    return (
        <section className="consignment-form-page bargain-form-page" aria-label="bargain-form-page">
            <section className="section-card consignment-form__header">
                <ZhDetailHeader title={id ? "编辑捡漏商品" : "新增捡漏商品"} />
            </section>
            <div className="bargain-form__body">
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Form
                    form={form}
                    className="consignment-form__form"
                    colon={false}
                    labelAlign="right"
                    labelCol={{ flex: "128px" }}
                    wrapperCol={{ flex: 1 }}
                >
                    <FormSection title="商品信息">
                        <ZhGrid cols={4} gap={[16, 0]}>
                            <ZhGridItem>
                                <Form.Item
                                    label="商品标题"
                                    name="title"
                                    rules={[{ required: true, message: "请输入商品标题" }]}
                                >
                                    <Input placeholder="请输入商品名称" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="权重值" name="weight">
                                    <ZhInputNumber className="w-full" min={0} placeholder="请输入权重值" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="捡漏单价" name="bargainUnitPrice" required>
                                    <ZhInputNumber className="w-full" min={0} placeholder="请输入捡漏单价" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="是否含税" required>
                                    {/* 保留 Vue 的原始值判断，避免接口回填字符串 "0" 时误删税率项。 */}
                                    {isIncludeTax === IS_INCLUDE_TAX.NO.code ? (
                                        <Form.Item name="isIncludeTax" noStyle>
                                            <ZhSelect
                                                aria-label="是否含税"
                                                options={IS_INCLUDE_TAX.options.map(option => ({
                                                    label: option.label,
                                                    value: Number(option.value)
                                                }))}
                                                placeholder="请选择是否含税"
                                                onChange={value => form.setFieldValue("isIncludeTax", value)}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Space.Compact className="w-full">
                                            <Form.Item name="isIncludeTax" noStyle>
                                                <ZhSelect
                                                    aria-label="是否含税"
                                                    options={IS_INCLUDE_TAX.options.map(option => ({
                                                        label: option.label,
                                                        value: Number(option.value)
                                                    }))}
                                                    placeholder="请选择是否含税"
                                                    onChange={value => {
                                                        form.setFieldValue("isIncludeTax", value);
                                                        if (Number(value) !== 1)
                                                            form.setFieldValue("taxRate", undefined);
                                                    }}
                                                />
                                            </Form.Item>
                                            <Form.Item name="taxRate" noStyle>
                                                <Select
                                                    aria-label="税率"
                                                    placeholder="请选择税率"
                                                    options={TAX_RATE.options.map(option => ({
                                                        label: option.label,
                                                        value: Number(option.value)
                                                    }))}
                                                />
                                            </Form.Item>
                                            {/* 旧版税率与是否含税共用一行，保留可访问名称但不额外占用布局。 */}
                                            <span className="consignment-form__legacy-control">税率</span>
                                        </Space.Compact>
                                    )}
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="市场价" name="unitPrice" required>
                                    <ZhInputNumber
                                        className="w-full"
                                        min={0}
                                        precision={2}
                                        placeholder="请输入市场价"
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="捡漏吨数" name="bargainTon" required>
                                    <ZhInputNumber className="w-full" min={0} placeholder="请输入捡漏吨数" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="总价">
                                    <span data-testid="bargain-total-cost">
                                        {totalCost == null ? "-" : `${totalCost} 元`}
                                    </span>
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="捡漏开始时间" name="bargainStartTime" required>
                                    <ZhDatePicker
                                        className="w-full"
                                        format="YYYY-MM-DD HH:mm"
                                        placeholder="请选择捡漏开始时间"
                                        type="datetime"
                                        valueFormat="YYYY-MM-DD HH:mm:ss"
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="活动天数" name="bargainCycle" required>
                                    <ZhSelect
                                        aria-label="活动天数"
                                        options={Array.from({ length: 30 }, (_, index) => ({
                                            label: `+${index + 1}`,
                                            value: index + 1
                                        }))}
                                        placeholder="请选择活动天数"
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="捡漏截止时间">
                                    <span>{bargainEndTime}</span>
                                </Form.Item>
                                {/* Vue 版截止时间为派生展示，但仍保留原始 datetime 控件契约供表单回显和提交。 */}
                                <div className="sr-only">
                                    <Form.Item name="bargainEndTime" noStyle>
                                        <ZhDatePicker
                                            format="YYYY-MM-DD HH:mm"
                                            placeholder="请选择捡漏截止时间"
                                            type="datetime"
                                            valueFormat="YYYY-MM-DD HH:mm:ss"
                                        />
                                    </Form.Item>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="广告观看时间" name="advertisingWatchTime" required>
                                    <ZhSelect
                                        aria-label="广告观看时间"
                                        options={ADVERTISING_WATCH_TIME.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        placeholder="请选择广告观看时间"
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="等待付款时间" name="waitingPayTime" required>
                                    <ZhSelect
                                        aria-label="等待付款时间"
                                        options={WAITING_PAY_TIME.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        placeholder="请选择等待付款时间"
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <Form.Item label="图片" required>
                                    <MediaUploadCard
                                        type="image"
                                        path={mediaPaths.imgMainPath || null}
                                        inputAriaLabel="上传捡漏商品主图"
                                        statusText={
                                            mediaIds.imgMainId ? `主图文件 ID：${mediaIds.imgMainId}` : undefined
                                        }
                                        onChange={event =>
                                            void handleMediaChange(event.currentTarget.files?.[0], "image")
                                        }
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <Form.Item label="视频" required>
                                    <MediaUploadCard
                                        type="video"
                                        path={mediaPaths.videoPath || null}
                                        inputAriaLabel="上传捡漏商品视频"
                                        statusText={mediaIds.videoId ? `视频文件 ID：${mediaIds.videoId}` : undefined}
                                        onChange={event =>
                                            void handleMediaChange(event.currentTarget.files?.[0], "video")
                                        }
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <Form.Item label="助力广告" required>
                                    <div className="bargain-form__advert-upload">
                                        <MediaUploadCard
                                            type="image"
                                            path={null}
                                            onChange={() => undefined}
                                            inputIdPrefix="bargain-ad-image"
                                        />
                                        <MediaUploadCard
                                            type="video"
                                            path={null}
                                            onChange={() => undefined}
                                            inputIdPrefix="bargain-ad-video"
                                        />
                                    </div>
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="供应商" name="supplierId" required>
                                    <Select
                                        aria-label="供应商"
                                        allowClear
                                        showSearch
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        onSearch={() => undefined}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="品类" name="productTypeId" required>
                                    <Select
                                        aria-label="品类"
                                        allowClear
                                        showSearch
                                        options={productTypeOptions}
                                        placeholder="请选择砂石品类"
                                        onChange={value => {
                                            const selected = productTypeOptions.find(item => item.value === value);
                                            setProductTypeTags(selected?.tags || []);
                                        }}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="规格" name="productTypeSpec" required>
                                    <Input placeholder="请输入规格" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="标签">
                                    <Input
                                        aria-label="品类标签"
                                        disabled
                                        value={productTypeTags.length ? productTypeTags.join(",") : "--"}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <Form.Item label="参数值" name="parameterInfo">
                                    <ParameterEditor />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <Form.Item label="商品标签" name="productLabel">
                                    <ProductLabelEditor />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <BargainRichTextItem
                                    form={form}
                                    name="introduce"
                                    label="商品介绍"
                                    required
                                    editorRef={introduceEditorRef}
                                />
                            </ZhGridItem>
                        </ZhGrid>
                    </FormSection>
                    <FormSection title="提货信息">
                        <ZhGrid cols={4} gap={[16, 0]}>
                            <ZhGridItem>
                                <Form.Item label="提货开始时间" name="deliveryStartTime" required>
                                    <Input placeholder="请选择提货开始时间" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="提货结束时间" name="deliveryEndTime" required>
                                    <Input placeholder="请选择提货结束时间" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="交付地区" name="areaCode" required>
                                    <Cascader
                                        aria-label="交付地区"
                                        options={areaOptions}
                                        placeholder="请选择交付地区"
                                        showSearch
                                        onChange={value => {
                                            const [provinceCode = "", cityCode = "", nextRegionCode = ""] =
                                                value.map(String);
                                            // 更换交付地区时清空旧地址和坐标，避免跨地区残留。
                                            form.setFieldsValue({
                                                provinceCode,
                                                cityCode,
                                                regionCode: nextRegionCode,
                                                address: "",
                                                geo: undefined
                                            });
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item name="provinceCode" hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="cityCode" hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="regionCode" hidden>
                                    <Input />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="详细地址" name="address" required>
                                    <AddressAutocomplete
                                        aria-label="详细地址"
                                        regionCode={regionCode}
                                        onSelectSuggestion={suggestion => {
                                            const areaCodes = getSuggestionAreaCodes(suggestion);
                                            const geo = getSuggestionGeo(suggestion);
                                            // 未选地区时从候选补齐行政区，选择候选后同步经纬度。
                                            if (!regionCode && areaCodes.length) {
                                                const [provinceCode = "", cityCode = "", nextRegionCode = ""] =
                                                    areaCodes;
                                                form.setFieldsValue({
                                                    areaCode: areaCodes,
                                                    provinceCode,
                                                    cityCode,
                                                    regionCode: nextRegionCode
                                                });
                                            }
                                            if (geo) form.setFieldValue("geo", geo);
                                        }}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={4}>
                                <BargainRichTextItem
                                    form={form}
                                    name="deliveryDescn"
                                    label="提货说明"
                                    editorRef={deliveryEditorRef}
                                />
                            </ZhGridItem>
                        </ZhGrid>
                    </FormSection>
                    <FormSection title="结算信息">
                        <ZhGrid cols={4} gap={[16, 0]}>
                            <ZhGridItem span={2}>
                                <Form.Item
                                    className="bargain-form__pay-types"
                                    label="支付方式"
                                    name="payTypes"
                                    required
                                >
                                    <ZhCheckboxGroup
                                        aria-label="支付方式"
                                        options={[
                                            {
                                                label: (
                                                    <span className="consignment-form__pay-type-label">
                                                        线上支付 <OnlineAccountTip />
                                                    </span>
                                                ),
                                                value: 1
                                            },
                                            { label: "线下支付", value: 2 }
                                        ]}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                            {payTypes?.includes(2) ? (
                                <ZhGridItem span={4}>
                                    <OfflineAccountEditor value={offlineAccounts} onChange={setOfflineAccounts} />
                                </ZhGridItem>
                            ) : null}
                            <ZhGridItem span={4}>
                                <BargainRichTextItem
                                    form={form}
                                    name="settlementDescn"
                                    label="结算说明"
                                    editorRef={settlementEditorRef}
                                />
                            </ZhGridItem>
                        </ZhGrid>
                    </FormSection>
                    <FormSection title="服务保障">
                        <BargainRichTextItem
                            form={form}
                            name="serviceGuarantee"
                            label="服务保障"
                            editorRef={serviceEditorRef}
                        />
                        <BargainRichTextItem
                            form={form}
                            name="regulation"
                            label="捡漏规则"
                            required
                            editorRef={regulationEditorRef}
                        />
                    </FormSection>
                    <FormSection title="其他信息">
                        <ZhGrid cols={4} gap={[16, 0]}>
                            <ZhGridItem>
                                <Form.Item label="商品专员" name="handlerId" required>
                                    <Input placeholder="请选择商品专员" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem span={3}>
                                <Form.Item label="商品专员联系电话" name="handlerMobile">
                                    <Input placeholder="请输入商品专员联系电话" />
                                </Form.Item>
                            </ZhGridItem>
                        </ZhGrid>
                    </FormSection>
                </Form>
                <Space className="operation-fixed-btn consignment-form__actions" size={0}>
                    <Button aria-label="保存捡漏商品" type="primary" onClick={() => void submit()}>
                        保存
                    </Button>
                    <Button aria-label="提交捡漏商品" type="primary" onClick={() => void submit()}>
                        提交
                    </Button>
                    <Button onClick={() => void navigate("/productManagement/bargain")}>返回</Button>
                </Space>
            </div>
        </section>
    );
}

export default BargainFormPage;
